import { useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Skeleton } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { enUS } from 'date-fns/locale';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import parent from '@/api/parent';
import ChildCard from '@/components/Parent/ChildCard';
import Navigation from '@/components/Navigation/Navigation';
import { route } from '@/constants/routeName';
import { DASHBOARD_STATUS_LABELS } from '@/helper/dashboardStatus';

const { getParentDashboard } = parent();

function todayIso() {
	return dayjs().format('YYYY-MM-DD');
}

export default function Dashboard() {
	const user = useSelector((state) => state.auth.user);
	const accountType = useSelector((state) => state.auth.accountType);
	const children = useSelector((state) => state.auth.children) || [];
	const childrenLoaded = useSelector((state) => state.auth.childrenLoaded);
	const key = useSelector((state) => state.auth.key);
	const accountId = user?.id;

	const [date, setDate] = useState(todayIso);

	const queryEnabled = Boolean(key && childrenLoaded && accountType !== undefined);

	const childrenKey = children.map((c) => c.id).join(',');

	const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
		queryKey: ['parent-dashboard', accountId, date, childrenKey],
		enabled: queryEnabled,
		placeholderData: keepPreviousData,
		queryFn: async () => {
			const res = await getParentDashboard({
				date,
				token: key,
				children,
			});
			return res.data;
		},
	});

	const summary = data?.summary;
	const cards = data?.children || [];

	const summaryParts = useMemo(() => {
		if (!summary) return null;
		return [
			{ count: summary.ordered ?? 0, label: 'ordered' },
			{ count: summary.not_ordered ?? 0, label: 'no order yet' },
			{ count: summary.cancelled ?? 0, label: 'cancelled' },
			{ count: summary.inactive ?? 0, label: 'inactive' },
		];
	}, [summary]);

	const showZeroChildren = childrenLoaded && children.length === 0 && accountType === 'parent';
	const showLoading = queryEnabled && !data && (isLoading || isFetching) && !isError;
	const showUpdating = Boolean(data) && isFetching && !isError;
	const showError = isError;

	return (
		<section className='app-container'>
			<div className='inner-container flex flex-col gap-4 px-4 pb-24 pt-4'>
				<header className='flex flex-col gap-0.5'>
					<p className='text-sm text-[var(--weak)]'>Family dashboard</p>
					<h1 className='text-xl font-bold text-black'>{user?.name || 'Parent'}</h1>
				</header>

				<div className='flex items-center gap-3'>
					<label htmlFor='dashboard-date' className='shrink-0 text-sm font-semibold text-black'>
						Meal date
					</label>
					<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={enUS}>
						<DatePicker
							className='date-picker'
							value={date ? dayjs(date) : null}
							onChange={(value) => {
								if (value) setDate(dayjs(value).format('YYYY-MM-DD'));
							}}
							format='DD/MM/YYYY'
							slotProps={{
								textField: {
									id: 'dashboard-date',
									size: 'small',
									inputProps: { 'aria-label': 'Meal date' },
								},
							}}
						/>
					</LocalizationProvider>
				</div>

				{showZeroChildren && (
					<div className='flex flex-col gap-2 rounded-md border border-black bg-white p-4'>
						<h2 className='font-bold text-black'>No children linked</h2>
						<p className='text-sm text-[var(--weak)]'>
							Ask the school office to link a student to this parent account. You stay signed in.
						</p>
						<Link to={`/${route.account}`} className='text-sm font-bold text-black underline'>
							Go to account
						</Link>
					</div>
				)}

				{!showZeroChildren && showLoading && (
					<div className='flex flex-col gap-3' role='status' aria-live='polite' aria-busy='true'>
						<p className='sr-only'>Loading family meal status…</p>
						<ChildCardSkeleton />
						<ChildCardSkeleton />
					</div>
				)}

				{!showZeroChildren && showError && (
					<div className='flex flex-col gap-3 rounded-md border border-black bg-white p-4' role='alert'>
						<p className='font-bold text-black'>Could not load dashboard</p>
						<p className='text-sm text-[var(--weak)]'>
							{error?.response?.data?.detail || error?.message || 'Something went wrong.'}
						</p>
						<p className='text-sm text-[var(--weak)]'>This is not the same as “No order yet”.</p>
						<button type='button' className='btn-actions h-11 rounded-md px-4 text-white' onClick={() => refetch()}>
							Retry
						</button>
					</div>
				)}

				{!showZeroChildren && data && !showError && (
					<>
						{showUpdating && (
							<p className='text-sm text-[var(--weak)]' role='status'>
								Updating meal status…
							</p>
						)}
						{summaryParts && (
							<ul
								className='flex flex-wrap gap-2'
								aria-live='polite'
								aria-busy={showUpdating}
								aria-label='Family meal status summary'
							>
								{summaryParts.map((part) => (
									<li
										key={part.label}
										className='inline-flex items-baseline gap-1 rounded border border-black bg-white px-2 py-1 text-sm'
									>
										<span className='font-bold text-black'>{part.count}</span>
										<span className='text-[var(--weak)]'>{part.label}</span>
									</li>
								))}
							</ul>
						)}
						<p className='sr-only'>
							Statuses: {Object.values(DASHBOARD_STATUS_LABELS).join(', ')}
						</p>
						<div className='flex flex-col gap-3'>
							{cards.map((child) => (
								<ChildCard key={child.id} child={child} />
							))}
						</div>
						{cards.length === 0 && (
							<p className='text-sm text-[var(--weak)]'>No children to show for this date.</p>
						)}
					</>
				)}
			</div>
			<Navigation />
		</section>
	);
}

function ChildCardSkeleton() {
	return (
		<div className='flex flex-col gap-3 rounded-md border border-black bg-white p-4' aria-hidden='true'>
			<div className='flex items-start justify-between gap-2'>
				<div className='flex min-w-0 flex-1 flex-col gap-1'>
					<Skeleton variant='text' width='72%' height={24} />
					<Skeleton variant='text' width='48%' height={18} />
				</div>
				<Skeleton variant='rounded' width={104} height={22} />
			</div>
			<Skeleton variant='text' width='36%' height={18} />
			<Skeleton variant='rounded' height={44} />
		</div>
	);
}
